import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { formatCep, onlyDigits, useViaCep } from '../useViaCep';
import { Loader2, MapPin } from 'lucide-react';
import type { OrcamentoDraftData } from '../draftStore';
import { Field, StepHeader } from '../ui';
import { inputCls, linkBtnCls } from '../styles';

type Cliente = OrcamentoDraftData['cliente'];

/**
 * Cidade e UF saem do CEP (ViaCEP), então viram texto em vez de dois campos.
 * Só abre edição manual se o CEP não for encontrado ou se pedirem.
 */
export function ClienteStep({
  cliente,
  setCliente,
  onAnimaisChange,
}: {
  cliente: Cliente;
  setCliente: Dispatch<SetStateAction<Cliente>>;
  onAnimaisChange: (v: string) => void;
}) {
  const patch = (p: Partial<Cliente>) => setCliente((c) => ({ ...c, ...p }));
  const [editLocal, setEditLocal] = useState(false);
  const cepStatus = useViaCep(cliente.cep, (l) => patch(l));

  // CEP não encontrado: abre cidade/UF pra digitar.
  useEffect(() => {
    if (cepStatus === 'notfound') setEditLocal(true);
  }, [cepStatus]);

  const local = [cliente.cidade, cliente.uf].filter(Boolean).join(', ');

  return (
    <div>
      <StepHeader
        title="Para quem é o orçamento"
        description="Nome, tamanho do rebanho e CEP. O CEP já traz a cidade e é usado pra cotar o frete."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Cliente ou fazenda">
          <input
            className={inputCls}
            value={cliente.nome}
            onChange={(e) => patch({ nome: e.target.value })}
            placeholder="Fazenda Santa Fé, João Pereira"
            autoFocus
          />
        </Field>

        <Field label="Número de animais" hint="Vira a quantidade de brincos: 1 por animal.">
          <input
            className={inputCls}
            inputMode="numeric"
            value={cliente.animais}
            onChange={(e) => onAnimaisChange(onlyDigits(e.target.value))}
            placeholder="500"
          />
        </Field>

        <Field
          label="CEP de entrega"
          hint={
            cepStatus === 'loading' ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" /> Buscando cidade…
              </span>
            ) : cepStatus === 'notfound' ? (
              'CEP não encontrado. Preencha cidade e UF abaixo.'
            ) : undefined
          }
        >
          <input
            className={inputCls}
            inputMode="numeric"
            value={cliente.cep}
            onChange={(e) => patch({ cep: formatCep(e.target.value) })}
            placeholder="78590-000"
          />
        </Field>

        <div className="flex flex-col justify-end">
          {editLocal ? (
            <div className="grid grid-cols-[1fr_72px] gap-3">
              <Field label="Cidade">
                <input
                  className={inputCls}
                  value={cliente.cidade}
                  onChange={(e) => patch({ cidade: e.target.value })}
                />
              </Field>
              <Field label="UF">
                <input
                  className={inputCls}
                  maxLength={2}
                  value={cliente.uf}
                  onChange={(e) => patch({ uf: e.target.value.toUpperCase() })}
                />
              </Field>
            </div>
          ) : (
            <p className="flex items-center gap-2 pb-2 text-sm">
              <MapPin size={15} className="shrink-0 text-text-secondary" />
              {local ? (
                <span className="text-text-title">{local}</span>
              ) : (
                <span className="text-text-secondary">
                  A cidade aparece quando o CEP for digitado
                </span>
              )}
              <button
                type="button"
                className={`${linkBtnCls} shrink-0 whitespace-nowrap`}
                onClick={() => setEditLocal(true)}
              >
                {local ? 'corrigir' : 'digitar à mão'}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
